import React from 'react'
import { useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { LockFilled } from 'components/lib/DesignSystem/Icons'
import Badge from 'components/lib/DesignSystem/V3/Badge'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'

import { FLYOUT_SIDE_OFFSET } from '../navStyles'

// Expanded: a bare grey padlock sitting at the right edge of the row (Figma
// 1:25784 — to the right of the chevron on locked sections, and trailing
// locked sub-pages; padlocks are grey, never blue). Collapsed: a small badge
// pinned to the icon's bottom-right.
const LockBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 6px;

  svg,
  svg path {
    fill: var(--sub-text-color);
  }

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      /* Figma 1:19324 — the padlock pins to the icon-button's top-right corner
         (node x=29,y=-1 in the 42px container), overlapping only the glyph's
         corner rather than sitting over its center (the old right:10px overlap). */
      position: absolute;
      top: -1px;
      right: -1px;
      margin-left: 0;

      svg,
      svg path {
        fill: var(--sub-text-color);
      }
    `}
`

// Upsell tooltip. Yellow panel does not flip in dark mode; pin text to blue-950.
const UPSELL_TEXT = 'var(--t-colors-blue-950)'

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* Fill the popup padding so the yellow panel is the text background. */
  margin: -10px;
  padding: 10px;
  border-radius: 8px;
  font-family: Poppins, sans-serif;
  background: var(--t-colors-yellow-100);

  &&,
  && * {
    color: ${UPSELL_TEXT};
  }
`

const TooltipTitleRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const TooltipTitle = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`

const TooltipSub = styled.span`
  font-size: 12px;
  line-height: 16px;
`

const Wrapper = styled.span`
  /* inline-flex (not display:contents) so the tabIndex element stays in the
     focus / accessibility tree — display:contents removes it in several browsers. */
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }

  /* Padlock turns orange on hover/keyboard-focus of the locked row. */
  &:hover ${LockBadge} svg,
  &:hover ${LockBadge} svg path,
  &:focus-visible ${LockBadge} svg,
  &:focus-visible ${LockBadge} svg path {
    fill: var(--warning-color);
  }

  /* Collapsed: the glyph and the padlock must warm together — key BOTH off this
     wrapper's hover, not the inner 42px button's. The wrapper spans the full 60px
     rail width (and the padlock overhangs the button's corner), so keying the
     glyph off the button alone left it grey while the padlock lit whenever the
     pointer sat on the rail gaps / padlock (or rested there with the flyout open).
     Colour every descendant glyph here so they're always all-or-nothing. */
  ${({ $collapsed }) =>
    $collapsed &&
    css`
      /* Outline glyphs (fill="none") stay a stroke. Filling them paints a solid
         amber shape; ATO and Deepfake only warm the line. */
      &:hover svg path:not([fill='none']),
      &:focus-visible svg path:not([fill='none']) {
        fill: var(--warning-color);
      }

      &:hover svg path[fill='none'],
      &:focus-visible svg path[fill='none'] {
        fill: none;
        stroke: var(--warning-color);
      }
    `}
`

/**
 * LockedNavItem — wraps a nav row to overlay upgrade tooltip + optional PLG click.
 *
 * AC4: upgrade tooltip title = node label; sub-text = "Upgrade to unlock this feature".
 * Click → PLG path if present, else inert. Keyboard accessible via tabIndex on inner span.
 *
 * @param {object} props
 * @param {string} props.label - item label shown as tooltip title
 * @param {string} [props.plgPath] - PLG destination path; if absent, click is inert
 * @param {React.ReactNode} props.children - the underlying row to render (locked appearance)
 * @param {string} [props.className]
 * @param {string} [props.dataPendoFeature] - Pendo Feature tag for the upsell click
 *   (placed on the wrapper — the real PLG click target for a locked row)
 */
const LockedNavItem = ({ label, plgPath, collapsed, children, className, dataPendoFeature }) => {
  const history = useHistory()

  const handleClick = (e) => {
    // Capture-phase guard: locked rows wrap the real <Link>, whose own onClick
    // (child, bubble phase) would history.push the locked route before a
    // bubble-phase handler here could block it. Capturing stops the event from
    // ever reaching the Link. No PLG destination → click is inert.
    e.preventDefault()
    e.stopPropagation()
    if (plgPath) history.push(plgPath)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleClick(e)
    }
  }

  const tooltipContent = (
    <TooltipContent>
      <TooltipTitleRow>
        <TooltipTitle>{label}</TooltipTitle>
        <Badge
          size="small"
          color="var(--t-colors-yellow-700)"
          textColor="var(--t-colors-base-white)"
        >
          PRO
        </Badge>
      </TooltipTitleRow>
      <TooltipSub>Upgrade to unlock this feature</TooltipSub>
    </TooltipContent>
  )

  return (
    <Tooltip
      content={tooltipContent}
      side="right"
      delay={150}
      surface="upsell"
      arrow
      connector="diamond"
      maxWidth={259}
      /* Shared with the main-nav / Settings flyouts so the diamond is the same
         distance from the rail here as everywhere else (was the default 8px,
         which tucked it against the rail). */
      sideOffset={FLYOUT_SIDE_OFFSET}
    >
      <Wrapper
        role={plgPath ? 'button' : undefined}
        tabIndex={0}
        aria-label={`${label} — Upgrade to unlock this feature`}
        data-pendo-feature={dataPendoFeature}
        $clickable={!!plgPath}
        $collapsed={collapsed}
        className={className}
        onClickCapture={handleClick}
        onKeyDown={handleKeyDown}
      >
        {children}
        <LockBadge $collapsed={collapsed} aria-hidden="true">
          <LockFilled noDefaultFill width="14px" height="14px" />
        </LockBadge>
      </Wrapper>
    </Tooltip>
  )
}

export default LockedNavItem
